/**
 * Array unique.
 * @param arr The array to remove duplicates from
 * @returns the unique elements of the array
 */
export const arrayUnique = <T>(arr: T[]) => Array.from(new Set(arr));

/**
 * Array unique for comparer for objects.
 * @param arr The array to remove duplicates from
 * @param comparer The comparer function to check for object equality. Should return true if the two objects are considered equal, and false otherwise
 * @returns A new array with the unique elements of the original array
 */
export const arrayUniqueWithComparer = <T>(arr: T[], comparer: (a: T, b: T) => boolean) => {
    const uniqueArr: T[] = [];

    arr.forEach((item) => {
        if (!uniqueArr.some((uniqueItem) => comparer(item, uniqueItem))) {
            uniqueArr.push(item);
        }
    });

    return uniqueArr;
};

/**
 * Array intersect.
 * The Set already has these functions, but they arent available in the browser yet :('
 * {@link https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Set }
 * @param arr1 One of the arrays to intersect
 * @param arr2 The second array to intersect
 * @returns The intersection of the two arrays
 */
export const arrayIntersect = <T>(arr1: T[], arr2: T[]) => arr1.filter(Set.prototype.has, new Set(arr2));

/**
 * Array diff. Note: The order of the arrays matter. The first array will be the one we remove elements from.
 * The Set already has these functions, but they arent available in the browser yet :('
 * {@link https://stackoverflow.com/a/1187628/1293256}
 * @param arr1 The array to use as the base for the diff
 * @param arr2 The array to compare with. All elements in this array will be removed from the return value, leaving the remainder from arr1
 * @returns The difference of the two arrays
 */
export const arrayDiff = <T>(arr1: T[], arr2: T[]) => arr1.filter((x) => !arr2.includes(x));

/**
 * Checks wether or not two arrays are equal
 * Equal meaning they both contain the same elements in the same order
 * Note: objects will be compared by reference
 */
export const arraysEqual = <T>(arr1: T[], arr2: T[]) => {
    return arr1.length === arr2.length && arr1.every((value, index) => value === arr2[index]);
};

/**
 * Checks wether or not two arrays are equal
 * Equal meaning they both contain the same elements, but not necessarily in the same order
 * Note: objects will be compared by reference
 */
export const arraysEqualIgnoreOrder = <T>(arr1: T[], arr2: T[]) => {
    return arr1.length === arr2.length && arr1.every((value) => arr2.includes(value));
};

/**
 * Converts an array to a map with keys provided by the keyGetter function.
 * @param arr The array we want to group into a map
 * @param keyGetter The key getter function to identify the keys in the map
 * @returns A map with the keys and the values grouped by the key.
 */
export const arrayGroupbyToMap = <T, TKey>(arr: T[], keyGetter: (item: T) => TKey) => {
    const map = new Map<TKey, T[]>();
    arr.forEach((item) => {
        const key = keyGetter(item);
        if (!map.has(key)) {
            map.set(key, []);
        }
        map.get(key)!.push(item);
    });
    return map;
};

/**
 * Creates a ring iterator for an array with a next and prev function
 * Its also possible to set an inital value, or set the current item or index
 * @param array The array we want to create a looping iterator for
 * @param initalValue The inital value we want to start at (OBS!, next will give the the item next to this one, not this one)
 * @returns An object with a next and prev function that loops through the array. At the ends it will loop back to the start/end.
 */
export const arrayToRingIterator = <T>(array: T[], initalValue?: T) => {
    let index = initalValue ? array.indexOf(initalValue) : -1;
    /**
     * Fetches the next item in the array. Starts from the beginning if we are at the end
     * @returns The next item in the array
     */
    const next = () => {
        index++;

        if (index > array.length - 1) {
            index = 0;
        }
        const item = array[index];

        return item;
    };

    /**
     * Fetches the previous item in the array. Starts from the end if we are at the beginning
     * @returns The previous item in the array.
     */
    const prev = () => {
        index--;

        if (index < 0) {
            index = array.length - 1;
        }
        const item = array[index];

        return item;
    };

    /**
     * Sets the current item to the one given. This means that next will give the item after this one
     * @param item The item we want to set as the current item
     */
    const setItem = (item: T) => {
        index = array.indexOf(item);
    };

    /**
     * Sets the current index to the one given. This means that next will give the item after the one at {@link indexToSet}
     * @param indexToSet The index we want to set as the current index
     */
    const setIndex = (indexToSet: number) => {
        index = indexToSet;
    };

    return {
        next,
        prev,
        setItem,
        setIndex,
    };
};

/**
 * Merges maps that has an array as the value
 *
 * Example:
 * ```typescript
 * const map1 = new Map<string, Array<number>>();
 * map1.set('a', [1, 2, 3]);
 * const map2 = new Map<string, Array<number>>();
 * map2.set('a', [3, 4, 5]);
 * const result = mergeMapsWithArrayValue(map1, map2);
 * // result.get('a') will be [1, 2, 3, 3, 4, 5]
 * ```
 * @param maps The maps to merge
 * @returns A new Map that contains all the keys from the input maps, and the values in a single array under that key
 */
export const mergeMapsWithArrayValue = <TKey, TValue>(...maps: Map<TKey, Array<TValue>>[]) => {
    const result = new Map<TKey, Array<TValue>>();

    maps.forEach((map) => {
        map.forEach((value, key) => {
            if (!result.has(key)) {
                result.set(key, []);
            }
            result.get(key)!.push(...value);
        });
    });

    return result;
};

/**
 * Creates window slices of an array
 * The windows will have the elements in the original array in all the positions of the window slices
 * Any spots that are not filled will be undefined.
 * This means that for the first window all the values of the window will be undefined except the last one, which will be the first element in the array.
 * The last window will only have the first element in the window filled, the rest will be undefined.
 * ```typescript
 * const arr = [1, 2, 3, 4, 5];
 * for (const window of arraySlidingWindow(arr, 3)) {
 *    console.log(window);
 * }
 * // Output:
 * // [undefined, undefined, 1]
 * // [undefined, 1, 2]
 * // [1, 2, 3]
 * // [2, 3, 4]
 * // [3, 4, 5]
 * // [4, 5, undefined]
 * // [5, undefined, undefined]
 * ```
 * @param arr The array to window
 * @param windowSize The size of the window
 * @returns A windowed iterator over the array
 */
export function arraySlidingWindow<T>(arr: T[], windowSize: number): (T | undefined)[][] {
    const result: (T | undefined)[][] = [];
    const current: (T | undefined)[] = Array(windowSize - 1).fill(undefined);

    for (const item of arr) {
        current.push(item);
        result.push([...current]);
        current.shift();
    }

    while (current.some((c) => c != undefined)) {
        current.push(undefined);
        result.push([...current]);
        current.shift();
    }

    return result;
}

/**
 * Helper function to add a value to a map that has an array as the value
 * @param map The map we want to add to
 * @param key The key we want to add the value under
 * @param value The value we want to add to the array
 */
export function addToMapWithArray<TKey, TValue>(map: Map<TKey, TValue[]>, key: TKey, value: TValue) {
    if (!map.has(key)) {
        map.set(key, []);
    }
    map.get(key)!.push(value);
}

/**
 * Helper function to add a value to a map that has an array as the value, ensuring that the value is unique according to the provided comparer
 * @param map The map we want to add to
 * @param key The key we want to add the value under
 * @param value The value to add
 * @param valueComparer A comparer function that checks every existing value in the array to see if the value to add is already present. If the value already exists it will be removed
 */
export function addToMapWithArrayAndEnsureTheValueIsUniqueInArrayByComparer<TKey, TValue>(
    map: Map<TKey, TValue[]>,
    key: TKey,
    value: TValue,
    valueComparer: (valueToAdd: TValue, valueAlreadyInArray: TValue) => boolean,
) {
    if (!map.has(key)) {
        map.set(key, []);
    }

    const existingValues = map.get(key)!;
    const indexOfExistingValue = existingValues.findIndex((existingValue) => valueComparer(value, existingValue));

    if (indexOfExistingValue !== -1) {
        // Value already exists, replace with the new value
        existingValues[indexOfExistingValue] = value;
    } else {
        // Add the new value
        existingValues.push(value);
    }
}

/**
 * Helper function to add a value to a map that has a set as the value
 * @param map The map we want to add to
 * @param key The key we want to add the value under
 * @param value The value we want to add to the set
 */
export function addToMapWithSet<TKey, TValue>(map: Map<TKey, Set<TValue>>, key: TKey, value: TValue) {
    if (!map.has(key)) {
        map.set(key, new Set<TValue>());
    }
    map.get(key)!.add(value);
}
